'use client';

import React from 'react';
import type { Airstrip, Leg, QuoteLegExtraServiceSelection, ResolvedExtraService } from '@/types';
import type { Advisory } from '../../services/advisoryRules';
import { AirstripInput, AncillaryToggle, LegInput } from './inputs';
import { MoonStar, Plane, Trash2, Cloud, Wind } from 'lucide-react';
import SectionAdvisories from './SectionAdvisories';

type QuoteLegCardProps = {
  leg: Leg;
  index: number;
  airstrips: Airstrip[];
  issueDate: string;
  originMetar?: { condition?: string; wind?: string; temp?: number; description?: string } | null;
  destinationMetar?: { condition?: string; wind?: string; temp?: number; description?: string } | null;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Leg>) => void;
  resolvedExtraServices: ResolvedExtraService[];
  sleepOverCharge?: number;
  advisories?: Advisory[];
};

const QuoteLegCard: React.FC<QuoteLegCardProps> = ({ leg, index, airstrips, issueDate, originMetar, destinationMetar, onRemove, onUpdate, resolvedExtraServices, sleepOverCharge = 0, advisories = [] }) => {
  const airstripOptions = airstrips.map(a => ({ code: a.code, name: a.name, icao: a.icao }));
  const [showExtraPanel, setShowExtraPanel] = React.useState(false);
  const isPositioningLeg = Boolean(leg.isDeadLeg) || Number(leg.paxCount || 0) === 0;
  const hasSleepOverCharge = Number(sleepOverCharge || 0) > 0;
  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const formatLocalTime = (date: Date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  const toDateValue = (value?: string) => {
    if (!value) return issueDate || '';
    if (value.includes('T')) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return formatLocalDate(parsed);
      return value.slice(0, 10);
    }
    if (/^\d{2}:\d{2}$/.test(value)) return issueDate || '';
    return issueDate || '';
  };
  const toTimeValue = (value?: string) => {
    if (!value) return '';
    if (/^\d{2}:\d{2}$/.test(value)) return value;
    if (value.includes('T')) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return formatLocalTime(parsed);
      return value.slice(11, 16);
    }
    return '';
  };
  const toIso = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return '';
    const normalized = `${dateStr}T${timeStr}:00`;
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString();
  };
  
  // Determine if leg is international
  const fromAirstrip = airstrips.find(as => as.code === leg.from);
  const toAirstrip = airstrips.find(as => as.code === leg.to);
  const isInternational = fromAirstrip && toAirstrip 
    ? (fromAirstrip.country !== toAirstrip.country)
    : false;
  const isImmigrationLeg = (() => {
    const id = String(leg.id || '').toUpperCase();
    if (id.startsWith('AUTO-IMM-') || id.includes('-AUTO-IMM-')) return true;
    const hasImmigrationAdvisory = advisories.some(item => String(item.id || '').includes('immigration'));
    if (hasImmigrationAdvisory) return true;
    const fromBorderPost = Boolean(fromAirstrip?.isBorderPost);
    const toBorderPost = Boolean(toAirstrip?.isBorderPost);
    return isInternational && (fromBorderPost || toBorderPost);
  })();

  const selections = leg.extraServiceSelections || [];
  const departureServices = resolvedExtraServices.filter(item => item.scope === 'ORIGIN');
  const destinationServices = resolvedExtraServices.filter(item => item.scope === 'DESTINATION');

  const normalizeBaseLabel = (raw: string) =>
    raw
      .replace(/\s*\((Departure|Destination)\)\s*$/i, '')
      .replace(/_DEP$|_ARR$/i, '')
      .trim();

  type AirportServiceRow = {
    key: string;
    label: string;
    departure: ResolvedExtraService | null;
    destination: ResolvedExtraService | null;
  };

  const serviceMap = [...departureServices, ...destinationServices].reduce<Map<string, AirportServiceRow>>((acc, service) => {
      const key = normalizeBaseLabel(service.name || service.code);
      if (!acc.has(key)) {
        acc.set(key, { key, label: key, departure: null, destination: null });
      }
      const row = acc.get(key)!;
      if (service.scope === 'ORIGIN') row.departure = service;
      if (service.scope === 'DESTINATION') row.destination = service;
      return acc;
    }, new Map<string, AirportServiceRow>());
  const airportServiceRows: AirportServiceRow[] = Array.from(serviceMap.values() as IterableIterator<AirportServiceRow>);

  const selectionKey = (item: Pick<QuoteLegExtraServiceSelection, 'serviceId' | 'scope'>) => `${item.serviceId}:${item.scope}`;
  const isSelected = (service: ResolvedExtraService) =>
    selections.some(item => selectionKey(item) === `${service.serviceId}:${service.scope}`);

  const upsertSelection = (service: ResolvedExtraService) => {
    if (isSelected(service)) {
      onUpdate(leg.id, {
        extraServiceSelections: selections.filter(
          item => selectionKey(item) !== `${service.serviceId}:${service.scope}`
        ),
      });
      return;
    }
    const next: QuoteLegExtraServiceSelection = {
      serviceId: service.serviceId,
      code: service.code,
      name: service.name,
      scope: service.scope,
      pricingModel: service.pricingModel,
      unitPrice: service.unitPrice,
      quantity: service.defaultQuantity || 1,
      source: service.source,
      contextCode: service.contextCode || null,
    };
    onUpdate(leg.id, {
      extraServiceSelections: [...selections, next],
    });
  };

  return (
    <div
      className={`relative rounded-[1.75rem] border transition-all group ${
        isPositioningLeg 
          ? 'bg-[linear-gradient(180deg,#fbfdff_0%,#f1f5f9_100%)] border-slate-200/90 shadow-[0_12px_30px_rgba(15,23,42,0.06)]' 
          : isInternational
            ? 'bg-[linear-gradient(180deg,#ffffff_0%,#fff8ed_100%)] border-2 border-amber-300/90 hover:border-amber-400 shadow-[0_16px_36px_rgba(245,158,11,0.10)]'
            : 'bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] border-slate-200/90 hover:border-sky-300/70 shadow-[0_12px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_18px_40px_rgba(14,165,233,0.08)]'
      } overflow-visible focus-within:z-[120]`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(248,250,252,0.92)_100%)]">
        <div className="flex items-center gap-3">
          <div className={`flex h-9 w-9 items-center justify-center rounded-2xl font-black text-xs shadow-sm ${
            isInternational 
              ? 'bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700 border border-amber-200' 
              : 'bg-gradient-to-br from-indigo-100 to-sky-100 text-indigo-700 border border-indigo-200'
          }`}>
            {String(index + 1).padStart(2, '0')}
          </div>
          {isInternational && (
            <span className="badge badge-compact bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-700 border border-amber-500/40 font-bold text-xs">
              INTERNATIONAL
            </span>
          )}
          {isPositioningLeg && (
            <span className="badge badge-compact bg-gradient-to-r from-sky-600 to-cyan-500 text-white font-bold text-xs border border-sky-300/70 shadow-sm">
              POSITIONING
            </span>
          )}
          {isImmigrationLeg && (
            <span className="badge badge-compact bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-bold text-xs border border-emerald-300/70 shadow-sm">
              IMMIGRATION
            </span>
          )}
          {hasSleepOverCharge && (
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-300/70 bg-gradient-to-r from-violet-600 to-indigo-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
              <MoonStar className="h-3 w-3" />
              Overnight
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(leg.id)}
          className="rounded-2xl border border-transparent p-2 text-slate-400 opacity-0 transition-all duration-200 group-hover:opacity-100 hover:border-red-200 hover:bg-red-50/80 hover:text-red-600"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="space-y-5 p-5">
        {(advisories.length > 0) && (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <SectionAdvisories title="Route Guard" section="ROUTE" advisories={advisories} compact />
            <SectionAdvisories title="Timing Guard" section="TIMING" advisories={advisories} compact />
          </div>
        )}
        <div className="rounded-[1.4rem] border border-slate-200/70 bg-white/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <AirstripInput 
            label="Origin" 
            value={leg.from} 
            options={airstripOptions} 
            onChange={v => onUpdate(leg.id, { from: v.toUpperCase() })} 
          />
          <AirstripInput 
            label="Destination" 
            value={leg.to} 
            options={airstripOptions} 
            onChange={v => onUpdate(leg.id, { to: v.toUpperCase() })} 
          />
          <LegInput
            label="PAX"
            type="number"
            value={leg.paxCount}
            onChange={v => onUpdate(leg.id, { paxCount: parseInt(v, 10) || 0, paxOB: parseInt(v, 10) || 0 })}
          />
          <LegInput
            label="Block Time"
            type="number"
            value={leg.blockTimeMinutes}
            placeholder="Minutes"
            onChange={v => onUpdate(leg.id, { blockTimeMinutes: parseInt(v, 10) || 0 })}
          />
        </div>
        </div>

        <div className="rounded-[1.4rem] border border-slate-200/70 bg-white/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <LegInput
            label="Date"
            type="date"
            value={toDateValue(leg.etd)}
            onChange={v => {
              const nextTime = toTimeValue(leg.etd) || '08:00';
              const iso = toIso(v, nextTime);
              onUpdate(leg.id, { etd: iso || leg.etd });
            }}
          />
          <LegInput
            label="ETD"
            type="time"
            value={toTimeValue(leg.etd)}
            onChange={v => {
              const baseDate = toDateValue(leg.etd) || issueDate || new Date().toISOString().split('T')[0];
              const iso = toIso(baseDate, v);
              onUpdate(leg.id, { etd: iso || leg.etd });
            }}
          />
          <LegInput
            label="ETA (optional)"
            type="time"
            placeholder="Auto"
            value={toTimeValue(leg.eta)}
            onChange={v => {
              const baseDate = toDateValue(leg.eta) || toDateValue(leg.etd) || issueDate || new Date().toISOString().split('T')[0];
              const iso = toIso(baseDate, v);
              onUpdate(leg.id, { eta: iso || leg.eta });
            }}
          />
        </div>
        </div>

        {/* Weather & Services Row */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[1.4rem] border border-slate-200/70 bg-white/75 px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]">
          {/* METAR for Origin and Destination */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Origin METAR */}
            <div className="flex items-center gap-2">
              <Cloud className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[10px] font-semibold text-slate-500 uppercase">Origin:</span>
              {originMetar ? (
                <>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      originMetar.condition === 'IFR'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : originMetar.condition === 'MVFR'
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : 'bg-teal-50 text-teal-700 border border-teal-200'
                    }`}
                  >
                    {originMetar.condition || 'VFR'}
                  </span>
                  {originMetar.wind && (
                    <span className="flex items-center gap-1 text-xs text-slate-600 font-medium">
                      <Wind className="w-3 h-3" />
                      {originMetar.wind}
                    </span>
                  )}
                  {originMetar.temp !== undefined && (
                    <span className="text-xs text-slate-600 font-medium">
                      {originMetar.temp}°C
                    </span>
                  )}
                </>
              ) : (
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                  No METAR
                </span>
              )}
            </div>

            {/* Destination METAR */}
            <div className="flex items-center gap-2">
              <Cloud className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[10px] font-semibold text-slate-500 uppercase">Dest:</span>
              {destinationMetar ? (
                <>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      destinationMetar.condition === 'IFR'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : destinationMetar.condition === 'MVFR'
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : 'bg-teal-50 text-teal-700 border border-teal-200'
                    }`}
                  >
                    {destinationMetar.condition || 'VFR'}
                  </span>
                  {destinationMetar.wind && (
                    <span className="flex items-center gap-1 text-xs text-slate-600 font-medium">
                      <Wind className="w-3 h-3" />
                      {destinationMetar.wind}
                    </span>
                  )}
                  {destinationMetar.temp !== undefined && (
                    <span className="text-xs text-slate-600 font-medium">
                      {destinationMetar.temp}°C
                    </span>
                  )}
                </>
              ) : (
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                  No METAR
                </span>
              )}
            </div>
          </div>

          {/* Ancillary Services */}
          {!isPositioningLeg && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                type="button"
                onClick={() => setShowExtraPanel(prev => !prev)}
                className={`btn-sm rounded-2xl px-4 py-2 transition-all duration-200 font-semibold ${
                  showExtraPanel || selections.length > 0
                    ? 'btn-premium btn-glow'
                    : 'border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50/70'
                }`}
              >
                Extra Services ({selections.length})
              </button>
              <div className="flex items-center gap-2 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-50/60 to-white px-3 py-2">
                <AncillaryToggle
                  label="Meet & Assist"
                  active={leg.meetAndAssistActive}
                  onToggle={() =>
                    onUpdate(leg.id, {
                      meetAndAssistActive: !leg.meetAndAssistActive,
                      meetAndAssistScope: leg.meetAndAssistScope || 'DESTINATION',
                    })
                  }
                />
                <button
                  type="button"
                  onClick={() => onUpdate(leg.id, { meetAndAssistScope: 'ORIGIN' })}
                  className={`px-3 py-1 rounded-md text-xs font-bold border transition-all ${
                    (leg.meetAndAssistScope || 'DESTINATION') === 'ORIGIN'
                      ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white border-indigo-500 shadow-md'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  Dep
                </button>
                <button
                  type="button"
                  onClick={() => onUpdate(leg.id, { meetAndAssistScope: 'DESTINATION' })}
                  className={`px-3 py-1 rounded-md text-xs font-bold border transition-all ${
                    (leg.meetAndAssistScope || 'DESTINATION') === 'DESTINATION'
                      ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white border-indigo-500 shadow-md'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  Dest
                </button>
              </div>
              <div className="flex items-center gap-2 rounded-2xl border border-teal-200/60 bg-gradient-to-r from-teal-50/60 to-white px-3 py-2">
                <AncillaryToggle
                  label="VIP Lounge"
                  active={leg.loungeActive}
                  onToggle={() =>
                    onUpdate(leg.id, {
                      loungeActive: !leg.loungeActive,
                      loungeScope: leg.loungeScope || 'DESTINATION',
                    })
                  }
                />
                <button
                  type="button"
                  onClick={() => onUpdate(leg.id, { loungeScope: 'ORIGIN' })}
                  className={`px-3 py-1 rounded-md text-xs font-bold border transition-all ${
                    (leg.loungeScope || 'DESTINATION') === 'ORIGIN'
                      ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white border-teal-500 shadow-md'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  Dep
                </button>
                <button
                  type="button"
                  onClick={() => onUpdate(leg.id, { loungeScope: 'DESTINATION' })}
                  className={`px-2 py-1 rounded text-[10px] font-bold border ${
                    (leg.loungeScope || 'DESTINATION') === 'DESTINATION'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-500 border-slate-300'
                  }`}
                >
                  Dep
                </button>
                <button
                  type="button"
                  onClick={() => onUpdate(leg.id, { loungeScope: 'DESTINATION' })}
                  className={`px-3 py-1 rounded-md text-xs font-bold border transition-all ${
                    (leg.loungeScope || 'DESTINATION') === 'DESTINATION'
                      ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white border-teal-500 shadow-md'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  Dest
                </button>
              </div>
            </div>
          )}
        </div>

        {showExtraPanel && !isPositioningLeg && (
          <div className="space-y-3 rounded-[1.4rem] border border-blue-200/70 bg-gradient-to-br from-blue-50/60 to-indigo-50/40 p-4 shadow-[0_12px_28px_rgba(59,130,246,0.08)]">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-700 flex items-center gap-2">
              ✨ Extra Services (Select Departure or Destination)
            </div>
            {airportServiceRows.length === 0 ? (
              <div className="text-xs text-slate-500">No airport-linked services available for this leg.</div>
            ) : (
              <div className="space-y-2">
                {airportServiceRows.map(row => (
                  <div key={row.key} className="rounded-lg border border-slate-200/70 bg-white/70 p-3 hover:bg-white/90 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-slate-800 mb-1">{row.label}</div>
                        <div className="text-[12px] text-slate-500 opacity-90">{(row.departure || row.destination) ? 'Choose departure or destination service' : 'No availability'}</div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <button
                          type="button"
                          disabled={!row.departure}
                          onClick={() => row.departure && upsertSelection(row.departure)}
                          className={`rounded-lg border px-3 py-2 text-left text-xs min-w-[140px] text-sm transition-all ${
                            row.departure && isSelected(row.departure)
                              ? 'border-blue-500 bg-blue-600 text-white'
                              : row.departure
                                ? 'border-slate-300 bg-white text-slate-700 hover:shadow-sm'
                                : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                          }`}
                        >
                          <div className="font-semibold">Departure</div>
                          <div className="opacity-80 text-xs mt-0.5">
                            {row.departure ? `$${row.departure.unitPrice.toFixed(2)} · ${row.departure.pricingModel}` : 'Not available'}
                          </div>
                        </button>
                        <button
                          type="button"
                          disabled={!row.destination}
                          onClick={() => row.destination && upsertSelection(row.destination)}
                          className={`rounded-lg border px-3 py-2 text-left text-xs min-w-[140px] text-sm transition-all ${
                            row.destination && isSelected(row.destination)
                              ? 'border-blue-500 bg-blue-600 text-white'
                              : row.destination
                                ? 'border-slate-300 bg-white text-slate-700 hover:shadow-sm'
                                : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                          }`}
                        >
                          <div className="font-semibold">Destination</div>
                          <div className="opacity-80 text-xs mt-0.5">
                            {row.destination ? `$${row.destination.unitPrice.toFixed(2)} · ${row.destination.pricingModel}` : 'Not available'}
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteLegCard;
